import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Catalog } from '@nousmedis/cc-types';
import { User } from '@nousmedis/guard-types/dist/frontend';
import { CatalogService } from 'app/modules/catalog-cloud/services/catalog.service';
import { UserService } from 'app/modules/user/services/user.service';
import { LangService } from 'app/modules/user/services/lang.service';
import { LayoutService } from './service/app.layout.service';
import { ApiService } from 'app/modules/api/services/api.service';

@Component({
    selector: 'app-menubar',
    templateUrl: './app.menubar.component.html',
})
export class AppMenubarComponent implements OnInit, OnDestroy {
    leftItems: MenuItem[] = [];
    userMenuItems: MenuItem[] = [];
    user: User;
    currentLang: string;

    private readonly THEME_KEY = 'CC_USER_THEME';

    private catalogSub: Subscription;

    constructor(
        private catalogService: CatalogService,
        private userService: UserService,
        private langService: LangService,
        private layoutService: LayoutService,
        private router: Router,
        private translate: TranslateService,
        private apiService: ApiService,
    ) {
        this.user = this.userService.getCurrentUser();
        this.currentLang = this.langService.getLang(this.user?.id) ?? 'es_ES';
    }

    ngOnInit(): void {
        this.loadSavedTheme();

        // Construir menú inmediatamente con lista vacía para que los items sean visibles
        this.buildMenu([]);

        this.catalogSub = this.catalogService.data$.subscribe((catalogs) => {
            this.buildMenu(catalogs);
        });

        // Cargar catálogos si aún no están en memoria
        this.catalogService.fetchCatalogList();
    }

    private loadSavedTheme(): void {
        // Aplicar desde localStorage inmediatamente para evitar flash
        const saved = localStorage.getItem(this.THEME_KEY);
        if (saved) {
            try {
                const { theme, colorScheme } = JSON.parse(saved);
                if (theme && colorScheme) {
                    this.layoutService.config.update((cfg) => ({ ...cfg, theme, colorScheme }));
                }
            } catch {}
        }

        // Verificar con la BD como fuente de verdad
        if (this.user?.id) {
            firstValueFrom(this.apiService.get(this.apiService.getBaseUrl() + '/api/v1/users/' + this.user.id))
                .then((userData: any) => {
                    if (userData?.theme && userData?.colorScheme) {
                        this.layoutService.config.update((cfg) => ({ ...cfg, theme: userData.theme, colorScheme: userData.colorScheme }));
                        localStorage.setItem(this.THEME_KEY, JSON.stringify({ theme: userData.theme, colorScheme: userData.colorScheme }));
                    }
                })
                .catch(() => {});
        }
    }

    ngOnDestroy(): void {
        this.catalogSub?.unsubscribe();
    }

    private buildMenu(catalogs: Catalog[]): void {
        const catalogItems: MenuItem[] = [
            {
                label: this.translate.instant('CATALOG.CREATE'),
                icon: 'pi pi-plus',
                command: () => this.router.navigate(['/catalog/new']),
            },
            { separator: true },
            ...catalogs.map((cat) => ({
                label: cat.name,
                icon: 'pi pi-book',
                command: () => this.router.navigate(['/catalog', cat.id, 'fascicles']),
            })),
        ];

        const langItems: MenuItem[] = [
            {
                label: 'Castellano',
                icon: this.currentLang === 'es_ES' ? 'pi pi-check' : 'pi pi-circle',
                command: () => this.changeLang('es_ES'),
            },
            {
                label: 'English',
                icon: this.currentLang === 'en_US' ? 'pi pi-check' : 'pi pi-circle',
                command: () => this.changeLang('en_US'),
            },
        ];

        const currentScheme = this.layoutService.config().colorScheme;
        const themeItems: MenuItem[] = [
            {
                label: this.translate.instant('THEME.SWITCH_LIGHT'),
                icon: currentScheme === 'light' ? 'pi pi-check' : 'pi pi-sun',
                command: () => this.changeTheme('cyan', 'light'),
            },
            {
                label: this.translate.instant('THEME.SWITCH_DIM'),
                icon: currentScheme === 'dim' ? 'pi pi-check' : 'pi pi-circle-fill',
                command: () => this.changeTheme('cyan', 'dim'),
            },
            {
                label: this.translate.instant('THEME.SWITCH_DARK'),
                icon: currentScheme === 'dark' ? 'pi pi-check' : 'pi pi-moon',
                command: () => this.changeTheme('cyan', 'dark'),
            },
        ];

        this.leftItems = [
            {
                label: this.translate.instant('MENU.HOME'),
                icon: 'pi pi-home',
                command: () => this.router.navigate(['/']),
            },
            {
                label: this.translate.instant('CATALOGS'),
                icon: 'pi pi-book',
                items: catalogItems,
            },
            {
                label: this.translate.instant('PAGE.TEMPLATE_LIST'),
                icon: 'pi pi-file',
                command: () => this.router.navigate(['/page-templates/list']),
            },
        ];

        this.userMenuItems = [
            // ── Idioma ──────────────────────────────────────────────────────
            { label: this.translate.instant('USER.SELECT_LANG'), disabled: true, styleClass: 'um-section-header' },
            ...langItems.map(i => ({ ...i, styleClass: 'um-item-indented' })),
            { separator: true },
            // ── Tema ────────────────────────────────────────────────────────
            { label: this.translate.instant('MENU.CHANGE_THEME'), disabled: true, styleClass: 'um-section-header' },
            ...themeItems.map(i => ({ ...i, styleClass: 'um-item-indented' })),
            { separator: true },
            // ── Otras instancias CC ─────────────────────────────────────────
            ...this.buildCcInstancesItems(),
            // ── Cerrar sesión ───────────────────────────────────────────────
            {
                label: this.translate.instant('USER.CLOSE_SESSION'),
                icon: 'pi pi-sign-out',
                command: () => this.logout(),
            },
        ];
    }

    private changeLang(lang: string): void {
        this.langService.setLang(this.user?.id, lang);
        location.reload();
    }

    private changeTheme(theme: string, colorScheme: 'light' | 'dark' | 'dim'): void {
        this.layoutService.config.update((cfg) => ({ ...cfg, theme, colorScheme }));

        // Persistir en localStorage
        localStorage.setItem(this.THEME_KEY, JSON.stringify({ theme, colorScheme }));

        // Persistir en BD (fire and forget)
        if (this.user?.id) {
            firstValueFrom(
                this.apiService.patch(this.apiService.getBaseUrl() + '/api/v1/users/' + this.user.id + '/theme', { theme, colorScheme })
            ).catch(() => {});
        }
    }

    private buildCcInstancesItems(): MenuItem[] {
        const raw = sessionStorage.getItem('cc_instances');
        if (!raw) return [];
        try {
            const instances: Array<{ organizacion: string; servicios: Array<{ servicio: string; url: string }> }> = JSON.parse(raw);
            if (!instances?.length) return [];
            const items: MenuItem[] = [
                { label: this.translate.instant('MENU.OTHER_CC_INSTANCES'), disabled: true, styleClass: 'um-section-header' },
            ];
            for (const org of instances) {
                items.push({ label: org.organizacion, disabled: true, styleClass: 'um-section-subheader' });
                for (const srv of org.servicios) {
                    items.push({
                        label: srv.servicio,
                        icon: 'pi pi-external-link',
                        styleClass: 'um-item-indented',
                        command: () => window.open(srv.url, '_blank'),
                    });
                }
            }
            items.push({ separator: true });
            return items;
        } catch {
            return [];
        }
    }

    get userName(): string {
        if (!this.user) return '';
        return [this.user.name, this.user.surname].filter(Boolean).join(' ');
    }

    get currentOrg(): string {
        if (this.userService.org?.name) return this.userService.org.name;
        const raw = sessionStorage.getItem('current_org');
        if (!raw) return '';
        try { return JSON.parse(raw)?.name ?? ''; } catch { return ''; }
    }

    get currentInstance(): string {
        return sessionStorage.getItem('current_instance') ?? '';
    }

    logout(): void {
        this.userService.logOut(this.user);
        window.location.href = '/login';
    }
}
